import React from 'react';
import { CanvasDimensions } from './useResponsiveCanvas';

interface PrologueSceneProps {
  dimensions: CanvasDimensions;
}

export const PrologueCataclysm: React.FC<PrologueSceneProps> = React.memo(({ dimensions }) => {
  const { vbWidth, vbHeight, isPortrait } = dimensions;
  const cx = vbWidth / 2;
  const summitX = cx;
  
  // Responsive scale factor
  const scale = isPortrait ? 0.72 : 1.0;

  // Mathematical vertical layout anchors
  const starY = vbHeight * 0.27;
  const peakY = starY + 28 * scale;
  const villageBaseY = vbHeight * 0.74;

  // 12 Ancestral Berber Glyphs placed at mathematically exact 30° radial increments
  // 270° is the 12 o'clock top zenith position, reserved for ⵣ (Yaz)
  const glyphOrbitals = [
    { char: 'ⵣ', angle: 270, delay: '0.0s' }, // 12 o'clock Top Zenith (Liberté / Yaz)
    { char: 'ⵡ', angle: 300, delay: '0.2s' }, // 1 o'clock
    { char: 'ⵏ', angle: 330, delay: '0.4s' }, // 2 o'clock
    { char: 'ⵜ', angle: 0,   delay: '0.6s' }, // 3 o'clock (East)
    { char: 'ⵙ', angle: 30,  delay: '0.8s' }, // 4 o'clock
    { char: 'ⵟ', angle: 60,  delay: '1.0s' }, // 5 o'clock
    { char: 'ⵃ', angle: 90,  delay: '1.2s' }, // 6 o'clock (South / Grounding)
    { char: 'ⴷ', angle: 120, delay: '1.4s' }, // 7 o'clock
    { char: 'ⴼ', angle: 150, delay: '1.6s' }, // 8 o'clock
    { char: 'ⵖ', angle: 180, delay: '1.8s' }, // 9 o'clock (West)
    { char: 'ⵎ', angle: 210, delay: '2.0s' }, // 10 o'clock
    { char: 'ⵕ', angle: 240, delay: '2.2s' }, // 11 o'clock
  ];

  const orbitalRadius = 175 * scale;
  const outerRingRadius = 225 * scale;
  const midRingRadius = 110 * scale;
  const innerRingRadius = 45 * scale;

  return (
    <svg
      className="w-full h-full absolute inset-0 select-none pointer-events-none"
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Deep Atmospheric Night Sky with Subtle Radial Emerald Illumination Emanating from Explosion */}
        <radialGradient
          id="cataclysmRadialSky"
          cx={summitX}
          cy={starY}
          r={Math.max(vbWidth, vbHeight) * 0.95}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#065f46" stopOpacity="0.9" />
          <stop offset="12%" stopColor="#044e3b" stopOpacity="0.82" />
          <stop offset="28%" stopColor="#03382b" stopOpacity="0.75" />
          <stop offset="50%" stopColor="#02271d" stopOpacity="0.85" />
          <stop offset="72%" stopColor="#011812" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#010a07" stopOpacity="1" />
        </radialGradient>

        {/* Deep Atmospheric Night Sky with Emerald Nebula Gradient */}
        <linearGradient id="cataclysmSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#010c09" />
          <stop offset="35%" stopColor="#021c16" />
          <stop offset="65%" stopColor="#03261e" />
          <stop offset="85%" stopColor="#021712" />
          <stop offset="100%" stopColor="#010d0a" />
        </linearGradient>

        {/* Supernova Celestial Radiant Core */}
        <radialGradient id="celestialCore" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="25%" stopColor="#ecfdf5" stopOpacity="0.95" />
          <stop offset="50%" stopColor="#6ee7b7" stopOpacity="0.75" />
          <stop offset="75%" stopColor="#10b981" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#047857" stopOpacity="0" />
        </radialGradient>

        {/* Radial Ambient Shockwave Glow */}
        <radialGradient id="ambientShockwave" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
          <stop offset="45%" stopColor="#059669" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#01140e" stopOpacity="0" />
        </radialGradient>

        {/* Mountain Blast Gradient: Smooth progressive transition from luminous white/mint at the peak down to dark green at village houses */}
        <linearGradient
          id="mountainBlastGrad"
          x1="0"
          y1={peakY}
          x2="0"
          y2={villageBaseY}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="3%" stopColor="#ecfdf5" />
          <stop offset="7%" stopColor="#d1fae5" />
          <stop offset="14%" stopColor="#6ee7b7" />
          <stop offset="25%" stopColor="#10b981" />
          <stop offset="42%" stopColor="#047857" />
          <stop offset="62%" stopColor="#064e3b" />
          <stop offset="82%" stopColor="#042a20" />
          <stop offset="100%" stopColor="#021611" />
        </linearGradient>

        {/* Luminous Summit Ridge Highlight Gradient (tightly centered at the apex) */}
        <linearGradient
          id="summitRidgeGlow"
          x1={summitX - 45 * scale}
          y1={peakY}
          x2={summitX + 45 * scale}
          y2={peakY}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#10b981" stopOpacity="0" />
          <stop offset="35%" stopColor="#6ee7b7" stopOpacity="0.7" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="65%" stopColor="#6ee7b7" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>

        {/* Distant Mountain Massif Gradient */}
        <linearGradient id="djurdjuraMassifGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#08382c" />
          <stop offset="35%" stopColor="#04221a" />
          <stop offset="70%" stopColor="#021611" />
          <stop offset="100%" stopColor="#010e0a" />
        </linearGradient>

        {/* Terracotta Tile Roofs (Aqrmud) */}
        <linearGradient id="roofGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#9a3412" />
          <stop offset="55%" stopColor="#6c230c" />
          <stop offset="100%" stopColor="#361005" />
        </linearGradient>

        {/* Dry-Stone Masonry Walls (Axxam) */}
        <linearGradient id="stoneWallGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a4035" />
          <stop offset="40%" stopColor="#102b23" />
          <stop offset="100%" stopColor="#061612" />
        </linearGradient>

        {/* Warm Golden Yellow Hearth Light for Village Windows */}
        <radialGradient id="windowWarmGlow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#fffbeb" stopOpacity="1" />
          <stop offset="30%" stopColor="#fde047" stopOpacity="0.95" />
          <stop offset="65%" stopColor="#f59e0b" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#b45309" stopOpacity="0.65" />
        </radialGradient>

        {/* Ambient Warm Golden Halo around windows */}
        <radialGradient id="windowAuraGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fde047" stopOpacity="0.45" />
          <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#b45309" stopOpacity="0" />
        </radialGradient>

        {/* Glow Filters */}
        <filter id="yellowLightFilter" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="emeraldGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="softGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.5" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ========================================================================= */}
      {/* 1. SEAMLESS CONTINUOUS DJURDJURA ALPINE MASSIF & SACRED SUMMIT             */}
      {/* ========================================================================= */}
      <rect x="0" y="0" width={vbWidth} height={vbHeight} fill="url(#cataclysmRadialSky)" />

      {/* Unified Continuous Mountain Massif with Elevated Central Peak Touching the Explosion */}
      <polygon
        points={`
          -100,${starY + 165}
          ${cx * 0.18},${starY + 130}
          ${cx * 0.38},${starY + 140}
          ${cx * 0.55},${starY + 110}
          ${cx * 0.72},${starY + 75}
          ${summitX - 70 * scale},${starY + 52 * scale}
          ${summitX - 35 * scale},${peakY + 12 * scale}
          ${summitX},${peakY}
          ${summitX + 35 * scale},${peakY + 14 * scale}
          ${summitX + 70 * scale},${starY + 55 * scale}
          ${cx * 1.28},${starY + 78}
          ${cx * 1.45},${starY + 112}
          ${cx * 1.65},${starY + 135}
          ${cx * 1.85},${starY + 120}
          ${vbWidth + 100},${starY + 165}
          ${vbWidth + 100},${vbHeight}
          -100,${vbHeight}
        `}
        fill="url(#mountainBlastGrad)"
      />

      {/* Illuminated Summit Ridge where Mountain Touches the White-Green Explosion */}
      <polyline
        points={`
          ${summitX - 45 * scale},${starY + 36 * scale}
          ${summitX - 20 * scale},${peakY + 6 * scale}
          ${summitX},${peakY}
          ${summitX + 20 * scale},${peakY + 7 * scale}
          ${summitX + 45 * scale},${starY + 38 * scale}
        `}
        fill="none"
        stroke="url(#summitRidgeGlow)"
        strokeWidth={2.0 * scale}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Central Sacred Mountain Ridge Spine descending and fading into the dark base */}
      <path
        d={`
          M ${summitX} ${peakY}
          Q ${summitX + 6 * scale} ${starY + 70 * scale}, ${summitX - 8 * scale} ${starY + 140 * scale}
        `}
        fill="none"
        stroke="#34d399"
        strokeWidth="1.0"
        strokeOpacity="0.3"
      />

      {/* ========================================================================= */}
      {/* 4. SACRED CELESTIAL ASTROLABE (Mathematically Exact 12-Axis Sacred Dial)  */}
      {/* ========================================================================= */}
      
      {/* Ambient Pulsing Shockwave Field */}
      <circle cx={summitX} cy={starY} r={230 * scale} fill="url(#ambientShockwave)">
        <animate attributeName="r" values={`${215 * scale}; ${245 * scale}; ${215 * scale}`} dur="3.5s" repeatCount="indefinite" />
      </circle>

      {/* 12 Mathematically Symmetric Radial Axes (Every 30°) */}
      <g stroke="#34d399" strokeWidth="1" strokeOpacity="0.4" strokeDasharray="3 3">
        {glyphOrbitals.map((g, idx) => {
          const rad = (g.angle * Math.PI) / 180;
          const x1 = summitX + Math.cos(rad) * innerRingRadius;
          const y1 = starY + Math.sin(rad) * innerRingRadius;
          const x2 = summitX + Math.cos(rad) * outerRingRadius;
          const y2 = starY + Math.sin(rad) * outerRingRadius;
          return (
            <line
              key={`radial_axis_${idx}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
            />
          );
        })}
      </g>

      {/* Concentric Astrolabe Circles */}
      <g fill="none" stroke="#34d399">
        {/* Inner Core Dial Ring */}
        <circle cx={summitX} cy={starY} r={innerRingRadius} strokeWidth="1.4" strokeOpacity="0.75" />
        
        {/* Mid Compass Ring with Decorative Dashes */}
        <circle cx={summitX} cy={starY} r={midRingRadius} strokeWidth="1.2" strokeDasharray="4 4" strokeOpacity="0.5" />
        
        {/* Main Glyph Orbital Ring */}
        <circle cx={summitX} cy={starY} r={orbitalRadius} strokeWidth="1" strokeOpacity="0.6" />
        
        {/* Outer Astrolabe Border Ring */}
        <circle cx={summitX} cy={starY} r={outerRingRadius} strokeWidth="1.2" strokeDasharray="8 4" strokeOpacity="0.35" />
      </g>

      {/* Expanding Harmonic Pulse Ring */}
      <circle cx={summitX} cy={starY} r={innerRingRadius} fill="none" stroke="#a7f3d0" strokeWidth="1.8" opacity="0.8">
        <animate attributeName="r" values={`${innerRingRadius}; ${outerRingRadius + 40}`} dur="2.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.85; 0" dur="2.8s" repeatCount="indefinite" />
      </circle>

      {/* Central Supernova Radiant Star Core */}
      <g transform={`translate(${summitX}, ${starY})`}>
        {/* Corona Flare */}
        <circle cx="0" cy="0" r={55 * scale} fill="url(#celestialCore)" />

        {/* 4 Primary Cardinal Diamond Rays (North, South, East, West) */}
        <path
          d="M 0 -80 Q 0 0 80 0 Q 0 0 0 80 Q 0 0 -80 0 Q 0 0 0 -80 Z"
          fill="#a7f3d0"
          opacity="0.85"
        >
          <animate attributeName="transform" type="rotate" from="0" to="360" dur="24s" repeatCount="indefinite" />
        </path>

        {/* 4 Secondary Diagonal Diamond Rays (45°, 135°, 225°, 315°) */}
        <path
          d="M 0 -50 Q 0 0 50 0 Q 0 0 0 50 Q 0 0 -50 0 Q 0 0 0 -50 Z"
          fill="#ffffff"
          opacity="0.95"
        >
          <animate attributeName="transform" type="rotate" from="45" to="405" dur="18s" repeatCount="indefinite" />
        </path>

        {/* White-Hot Luminous Center Point */}
        <circle cx="0" cy="0" r={12 * scale} fill="#ffffff" filter="url(#emeraldGlow)" />
        <circle cx="0" cy="0" r={5 * scale} fill="#ffffff" />
      </g>

      {/* 12 Tifinagh Glyphs along the Harmonic 30° Orbit */}
      {glyphOrbitals.map((g, idx) => {
        const rad = (g.angle * Math.PI) / 180;
        const gx = summitX + Math.cos(rad) * orbitalRadius;
        const gy = starY + Math.sin(rad) * orbitalRadius;
        const isYaz = g.char === 'ⵣ';

        return (
          <g key={`orbital_glyph_${idx}`} transform={`translate(${gx}, ${gy})`}>
            {/* Background Disk */}
            <circle
              cx="0"
              cy="0"
              r={isYaz ? 19 : 17}
              fill="#021a13"
              stroke={isYaz ? '#6ee7b7' : '#34d399'}
              strokeWidth={isYaz ? '1.8' : '1.2'}
              strokeOpacity={isYaz ? '1' : '0.8'}
            />
            {/* Tifinagh Character */}
            <text
              x="0"
              y="0"
              fill={isYaz ? '#ffffff' : '#ecfdf5'}
              fontSize={isYaz ? 23 : 19}
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="central"
              filter="url(#softGlow)"
              className="select-none font-sans"
            >
              {g.char}
            </text>
          </g>
        );
      })}

      {/* ========================================================================= */}
      {/* 5. AUTHENTIC TERRACED KABYLE VILLAGE (Taddart n Lǧemɛa)                    */}
      {/* ========================================================================= */}

      {/* ------------------------------------------------------------------------- */}
      {/* TERRACE 1 (Upper Left Promontory): Watchman House & Sentinel              */}
      {/* ------------------------------------------------------------------------- */}
      <g transform={`translate(${cx - 245 * scale}, ${villageBaseY - 38})`}>
        {/* Watchman House (Axxam) */}
        <rect x="0" y="16" width="55" height="38" rx="2" fill="url(#stoneWallGrad)" stroke="#03241b" strokeWidth="0.8" />
        
        {/* Terracotta Tile Roof (Aqrmud) */}
        <polygon points="-5,16 27,-4 60,16" fill="url(#roofGrad)" />
        <polyline points="-5,16 27,-4 60,16" fill="none" stroke="#6ee7b7" strokeWidth="1.6" strokeLinecap="round" />

        {/* Traditional Entrance & Windows (Tops Aligned at y=28) with Warm Yellowish Light */}
        <rect x="22" y="28" width="12" height="26" rx="1" fill="#020d09" />
        
        {/* Left Window */}
        <rect x="5" y="25" width="14" height="14" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="8" y="28" width="8" height="8" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="9" y="29" width="6" height="6" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />

        {/* Right Window */}
        <rect x="35" y="25" width="14" height="14" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="38" y="28" width="8" height="8" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="39" y="29" width="6" height="6" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />
      </g>

      {/* ------------------------------------------------------------------------- */}
      {/* TERRACE 2 (Mid-Left Slope): Family House in Silhouette (Foreground)       */}
      {/* ------------------------------------------------------------------------- */}
      <g transform={`translate(${cx - 130 * scale}, ${villageBaseY - 14})`}>
        {/* House Body */}
        <rect x="0" y="14" width="64" height="38" rx="2" fill="url(#stoneWallGrad)" stroke="#03241b" strokeWidth="0.8" />
        
        {/* Roof passing in front */}
        <polygon points="-5,14 32,-8 69,14" fill="url(#roofGrad)" />
        <polyline points="-5,14 32,-8 69,14" fill="none" stroke="#6ee7b7" strokeWidth="1.6" strokeLinecap="round" />

        {/* Traditional Entrance & Windows (Tops Aligned at y=26) with Warm Yellowish Light */}
        <rect x="38" y="26" width="14" height="26" rx="1" fill="#020a07" />
        
        {/* Left Window 1 */}
        <rect x="7" y="23" width="15" height="15" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="10" y="26" width="9" height="9" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="11" y="27" width="7" height="7" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />
        
        {/* Left Window 2 */}
        <rect x="20" y="23" width="15" height="15" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="23" y="26" width="9" height="9" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="24" y="27" width="7" height="7" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />
      </g>

      {/* ------------------------------------------------------------------------- */}
      {/* TERRACE 4 (Center-Right Terrace): Traditional Stone House                 */}
      {/* ------------------------------------------------------------------------- */}
      <g transform={`translate(${cx + 70 * scale}, ${villageBaseY - 10})`}>
        {/* House Body */}
        <rect x="0" y="14" width="58" height="36" rx="2" fill="url(#stoneWallGrad)" stroke="#03241b" strokeWidth="0.8" />
        
        {/* Roof */}
        <polygon points="-5,14 29,-7 63,14" fill="url(#roofGrad)" />
        <polyline points="-5,14 29,-7 63,14" fill="none" stroke="#6ee7b7" strokeWidth="1.6" strokeLinecap="round" />

        {/* Door & Window (Tops Aligned at y=26) with Warm Yellowish Light */}
        <rect x="34" y="26" width="13" height="24" rx="1" fill="#020a07" />
        <rect x="7" y="23" width="15" height="15" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="10" y="26" width="9" height="9" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="11" y="27" width="7" height="7" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />
      </g>

      {/* ------------------------------------------------------------------------- */}
      {/* TERRACE 5 (Far Right Ledge): Stepped House & Villager in Awe              */}
      {/* ------------------------------------------------------------------------- */}
      <g transform={`translate(${cx + 175 * scale}, ${villageBaseY - 30})`}>
        {/* House Body */}
        <rect x="0" y="16" width="62" height="42" rx="2" fill="url(#stoneWallGrad)" stroke="#03241b" strokeWidth="0.8" />
        
        {/* Roof */}
        <polygon points="-5,16 31,-8 67,16" fill="url(#roofGrad)" />
        <polyline points="-5,16 31,-8 67,16" fill="none" stroke="#6ee7b7" strokeWidth="1.6" strokeLinecap="round" />

        {/* Door & Flanking Windows (Tops Aligned at y=28) with Warm Yellowish Light */}
        <rect x="25" y="28" width="13" height="30" rx="1" fill="#020a07" />
        
        {/* Left Window */}
        <rect x="5" y="25" width="15" height="16" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="8" y="28" width="9" height="10" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="9" y="29" width="7" height="8" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />
        
        {/* Right Window */}
        <rect x="41" y="25" width="15" height="16" rx="3" fill="url(#windowAuraGlow)" />
        <rect x="44" y="28" width="9" height="10" rx="1" fill="#451a03" stroke="#b45309" strokeWidth="0.6" />
        <rect x="45" y="29" width="7" height="8" rx="0.5" fill="url(#windowWarmGlow)" filter="url(#yellowLightFilter)" />
      </g>

      {/* ========================================================================= */}
      {/* 6. FOREGROUND VIGNETTE                                                     */}
      {/* ========================================================================= */}
      <rect
        x="0"
        y={vbHeight - 20}
        width={vbWidth}
        height={20}
        fill="#010a07"
      />
    </svg>
  );
});
